import { API_BASE } from "./apiBase";

function urlBase64ToUint8Array(base64String) {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const rawData = atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; ++i) outputArray[i] = rawData.charCodeAt(i);
  return outputArray;
}

export function pushSupported() {
  return (
    typeof window !== "undefined" &&
    "serviceWorker" in navigator &&
    "PushManager" in window &&
    "Notification" in window
  );
}

export function notificationPermission() {
  if (typeof window === "undefined" || !("Notification" in window)) return "unsupported";
  return Notification.permission; // "default" | "granted" | "denied"
}

export function isStandalonePWA() {
  if (typeof window === "undefined") return false;
  return (
    (typeof window.matchMedia === "function" && window.matchMedia("(display-mode: standalone)").matches) ||
    window.navigator.standalone === true
  );
}

export function isIOS() {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent || "";
  const iOSDevice = /iPad|iPhone|iPod/.test(ua);
  // iPadOS 13+ reports as Mac; detect by touch points.
  const iPadOS = navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1;
  return iOSDevice || iPadOS;
}

/**
 * Reconcile the browser's push subscription with the user's preference.
 *
 * - enabled=false: unsubscribe locally and tell the backend to drop it.
 * - enabled=true:
 *     - promptIfNeeded=false (default, "silent"): subscribe only if permission is already
 *       "granted". Never shows the permission prompt - safe to call on load.
 *     - promptIfNeeded=true ("gesture"): may call Notification.requestPermission(); call this
 *       only from an explicit user action (button/toggle).
 *
 * Returns { supported, permission, subscribed }.
 */
export async function syncBrowserPushSubscription(token, enabled, { promptIfNeeded = false } = {}) {
  if (!token || !pushSupported()) {
    return { supported: false, permission: notificationPermission(), subscribed: false };
  }

  const headers = {
    Authorization: `Bearer ${token}`,
    "Content-Type": "application/json",
  };

  const reg = await navigator.serviceWorker.ready;
  const existing = await reg.pushManager.getSubscription();

  if (!enabled) {
    if (existing) {
      const endpoint = existing.endpoint;
      await existing.unsubscribe();
      await fetch(`${API_BASE}/users/me/browser-push-subscription?endpoint=${encodeURIComponent(endpoint)}`, {
        method: "DELETE",
        headers,
        cache: "no-store",
      });
    }
    return { supported: true, permission: notificationPermission(), subscribed: false };
  }

  if (Notification.permission === "default") {
    if (!promptIfNeeded) return { supported: true, permission: "default", subscribed: false };
    await Notification.requestPermission();
  }
  if (Notification.permission !== "granted") {
    return { supported: true, permission: Notification.permission, subscribed: false };
  }

  const keyRes = await fetch(`${API_BASE}/users/browser-push/public-key`, { headers, cache: "no-store" });
  if (!keyRes.ok) return { supported: true, permission: "granted", subscribed: !!existing };
  const keyData = await keyRes.json();
  if (!keyData?.configured || !keyData?.public_key) {
    return { supported: true, permission: "granted", subscribed: !!existing };
  }

  const sub =
    existing ||
    (await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(keyData.public_key),
    }));

  await fetch(`${API_BASE}/users/me/browser-push-subscription`, {
    method: "PUT",
    headers,
    body: JSON.stringify(sub.toJSON()),
    cache: "no-store",
  });
  return { supported: true, permission: "granted", subscribed: true };
}
